import { useEffect, useRef, useState } from 'react';

import {
  applyStaleWhileEmpty,
  initialStaleState,
  type PollPayload,
  type StaleOutput,
  type StaleState,
} from './staleWhileEmpty';

// TSMED-1482: owns the stale-while-empty reducer state across poll responses.
// `responseKey` identifies a poll (use the query's fulfilled timestamp): a
// re-render carrying the same key is not a new response and must not advance the
// empty streak.
//
// Display-only. The empty-response alert signal (logEmptyResponseToSentry) is
// emitted UPSTREAM in validateAndTransformResponse, so every empty poll is still
// reported to Sentry even though the board keeps showing last-good data. This
// layer makes empties seamless for the operator, NOT for the alert system — do
// not move the empty-check here or otherwise gate that signal.
export const DEFAULT_STALE_THRESHOLD = 3;

export const useStaleWhileEmpty = <TItem>(
  incoming: PollPayload<TItem>,
  responseKey: number | string | null,
  threshold: number = DEFAULT_STALE_THRESHOLD,
): StaleOutput<TItem> => {
  const stateRef = useRef<StaleState<TItem>>(initialStaleState<TItem>());
  const processedKeyRef = useRef<number | string | null | undefined>(undefined);
  const [output, setOutput] = useState<StaleOutput<TItem>>(() => ({
    data: incoming.data,
    generatedAt: incoming.generatedAt,
    isStale: false,
  }));

  useEffect(() => {
    // Process each response exactly once; ignore unrelated re-renders (the
    // fresh `incoming` object identity each render is guarded by `responseKey`).
    if (responseKey === processedKeyRef.current) {
      return;
    }
    processedKeyRef.current = responseKey;
    const next = applyStaleWhileEmpty(stateRef.current, incoming, threshold);
    stateRef.current = next.state;
    setOutput(next.output);
  }, [responseKey, threshold, incoming]);

  return output;
};
